import React, { useState, useRef, useEffect } from 'react';

interface ExportDropdownProps {
  analysisId: string;
  analysisData?: any; // Full analysis response from API
}

/**
 * Generate a Markdown string from the analysis data.
 */
function generateMarkdown(data: any): string {
  const { projectReview, repository, analysis: meta, intelligenceReport } = data || {};
  const cq = projectReview?.codeQuality || {};
  const emp = projectReview?.employabilitySignal || {};
  const auth = projectReview?.projectAuthenticity || {};
  const strengths = projectReview?.strengths || [];
  const weaknesses = projectReview?.weaknesses || [];
  const resumeBullets = intelligenceReport?.resumeBullets || [];
  const questions = data?.interviewSimulation?.questions || [];
  const repoName = repository?.repositoryName || meta?.repositoryName || meta?.repositoryUrl || 'Repository';

  // Realistic Scaling Factor
  const currentScore = emp.overall ?? 0;
  const availableHeadroom = 100 - currentScore;
  const pointsMap: Record<string, number> = { high: 15, medium: 10, low: 5 };
  const quickWins = [...weaknesses]
    .sort((a: any, b: any) => {
      const sv = (s?: string) => s === 'high' ? 3 : s === 'medium' ? 2 : 1;
      return sv(b.severity) - sv(a.severity);
    })
    .slice(0, 3);
  const totalBasePoints = quickWins.reduce((acc, w) => acc + (pointsMap[w.severity] ?? 8), 0);
  const realismFactor = totalBasePoints > 0 ? Math.min(1, (availableHeadroom * 0.8) / totalBasePoints) : 1;

  let md = '';
  md += `# Career Impact Report — ${repoName}\n\n`;
  md += `> Generated by DevContext.AI on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}\n\n`;

  if (meta?.repositoryUrl) md += `**Repository:** ${meta.repositoryUrl}\n\n`;

  // ── Scores ──
  md += `## Scores Overview\n\n`;
  md += `| Metric | Score |\n|---|---|\n`;
  md += `| **Employability Signal** | ${emp.overall ?? '—'}/100 |\n`;
  md += `| **Code Quality** | ${cq.overall ?? '—'}/100 |\n`;
  md += `| **Authenticity** | ${auth.score ?? '—'}/100 |\n`;
  md += `\n`;

  // ── Company Tier Match ──
  if (emp.companyTierMatch) {
    md += `## Company Tier Match\n\n`;
    md += `| Tier | Score |\n|---|---|\n`;
    md += `| Big Tech / FAANG | ${emp.companyTierMatch.bigTech ?? '—'} |\n`;
    md += `| Product Companies | ${emp.companyTierMatch.productCompanies ?? '—'} |\n`;
    md += `| Startups | ${emp.companyTierMatch.startups ?? '—'} |\n`;
    md += `| Service Companies | ${emp.companyTierMatch.serviceCompanies ?? '—'} |\n`;
    md += `\n`;
  }

  // ── Code Quality Breakdown ──
  md += `## Code Quality Breakdown\n\n`;
  md += `| Category | Score |\n|---|---|\n`;
  for (const key of ['readability', 'maintainability', 'errorHandling', 'security', 'performance', 'documentation']) {
    if (cq[key] !== undefined) {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (s: string) => s.toUpperCase());
      md += `| ${label} | ${cq[key]}/100 |\n`;
    }
  }
  md += `\n`;

  // ── Tech Stack ──
  if (repository?.frameworks?.length) {
    md += `## Tech Stack\n\n`;
    md += repository.frameworks.map((f: string) => `- ${f}`).join('\n') + '\n\n';
  }

  // ── Quick Wins ──
  if (quickWins.length > 0) {
    md += `## Quick Wins\n\n`;
    quickWins.forEach((w: any, i: number) => {
      const basePts = pointsMap[w.severity] ?? 8;
      const pts = Math.max(1, Math.floor(basePts * realismFactor));
      md += `${i + 1}. **${w.issue}** (+${pts} pts)\n`;
      if (w.recommendation) md += `   - Fix: ${w.recommendation}\n`;
      md += `\n`;
    });
  }

  // ── Strengths ──
  if (strengths.length > 0) {
    md += `## Strengths (${strengths.length})\n\n`;
    strengths.forEach((s: any) => {
      md += `- **${s.pattern}** — ${s.description}\n`;
      if (s.impact) md += `  - Impact: ${s.impact}\n`;
    });
    md += `\n`;
  }

  // ── Weaknesses ──
  if (weaknesses.length > 0) {
    md += `## Areas for Improvement (${weaknesses.length})\n\n`;
    weaknesses.forEach((w: any) => {
      md += `- **${w.issue}** [${w.severity || 'medium'}]\n`;
      if (w.impact) md += `  - Impact: ${w.impact}\n`;
      if (w.recommendation) md += `  - Fix: ${w.recommendation}\n`;
    });
    md += `\n`;
  }

  // ── Interview Questions ──
  if (questions.length > 0) {
    md += `## Comprehensive Question Sheet (${questions.length} Questions)\n\n`;
    questions.forEach((q: any, i: number) => {
      md += `### Q${i + 1}: ${q.question}\n`;
      md += `- **Category:** ${q.category} | **Difficulty:** ${q.difficulty}\n`;
      if (q.groundedIn?.length > 0) {
        md += `- **Grounded in:** ${q.groundedIn.map((r: any) => `\`${r.file.split('/').pop()}\``).join(', ')}\n`;
      }
      md += `\n`;
    });
  }

  // ── Resume Bullets ──
  if (resumeBullets.length > 0) {
    md += `## Resume-Ready Bullets\n\n`;
    resumeBullets.forEach((b: any) => {
      md += `- ${typeof b === 'string' ? b : b.bullet || b.text || JSON.stringify(b)}\n`;
    });
    md += `\n`;
  }

  md += `---\n*Powered by DevContext.AI — AI-driven code intelligence*\n`;
  return md;
}

/**
 * Trigger a download of a text blob as a file.
 */
function downloadAsFile(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger high-fidelity PDF export via browser print
 */
function downloadAsPdf() {
  window.print();
}

const ExportDropdown: React.FC<ExportDropdownProps> = ({ analysisId, analysisData }) => {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [fetchedData, setFetchedData] = useState<any>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getData = async (): Promise<any> => {
    if (analysisData) return analysisData;
    if (fetchedData) return fetchedData;
    // Fetch on-demand if not passed in
    const { default: api } = await import('../../services/api');
    const data = await api.getAnalysis(analysisId);
    setFetchedData(data);
    return data;
  };

  const handleExport = async (format: 'pdf' | 'markdown') => {
    setExporting(format);
    try {
      const data = await getData();
      const repoName = data?.repository?.repositoryName
        || data?.analysis?.repositoryName
        || 'analysis';
      if (format === 'markdown') {
        const md = generateMarkdown(data);
        downloadAsFile(md, `${repoName}-career-impact.md`, 'text/markdown');
      } else {
        downloadAsPdf();
      }
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(null);
      setOpen(false);
    }
  };

  return (
    <div className="export-dropdown-wrap" style={{ position: 'relative' }} ref={wrapRef}>
      <button
        className="btn-ghost"
        onClick={() => setOpen(!open)}
        style={{ width: 'auto', fontSize: '12px', padding: '7px 14px', marginTop: 0 }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <>
          <div className="export-backdrop" onClick={() => setOpen(false)} />
          <div className="export-dropdown">
            <button className="export-option" onClick={() => handleExport('pdf')} disabled={!!exporting}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div>
                <div className="export-option-title">Export as PDF</div>
                <div className="export-option-sub">Opens print dialog for save-as-PDF</div>
              </div>
              {exporting === 'pdf' && <div className="spinner-sm" />}
            </button>
            <button className="export-option" onClick={() => handleExport('markdown')} disabled={!!exporting}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <div>
                <div className="export-option-title">Export as Markdown</div>
                <div className="export-option-sub">Downloads .md file</div>
              </div>
              {exporting === 'markdown' && <div className="spinner-sm" />}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportDropdown;
